import React from 'react';
import { useRecoilState } from 'recoil';

import * as analytics from '@gitbook/app-analytics/client';
import { ButtonTab, ButtonTabs, FieldGroup, Label, Switch } from '@gitbook/spine-react';

import { DiffContext } from '../Diff';
import { diffOptionsState } from '../Diff/options';
import { type TableOfContentsTab, TableOfContentsTabState } from './state';

const options: Array<{
    value: TableOfContentsTab;
    label: string;
}> = [
    {
        value: 'pages',
        label: 'Pages',
    },
    {
        value: 'reusable-content',
        label: 'Reusable content',
    },
    {
        value: 'files',
        label: 'Files',
    },
];

/**
 * Header with navigation tabs in the table of contents.
 */
export function TableOfContentsHeader(props: {
    readOnly: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { readOnly, analyticsContext } = props;
    const { diffMode } = React.useContext(DiffContext);

    if (readOnly && !diffMode) {
        return null;
    }

    return (
        <div className="flex items-center border-b border-b-base px-5 py-2">
            {readOnly ? null : <HeaderEditorTabs />}
            {diffMode ? <HeaderDiff analyticsContext={analyticsContext} /> : null}
        </div>
    );
}

/**
 * Header with navigation tabs in the table of contents.
 */
function HeaderEditorTabs() {
    const { tab, setTab } = React.useContext(TableOfContentsTabState);

    return (
        <ButtonTabs>
            {options.map((option) => {
                const isActive = option.value === tab;
                return (
                    <ButtonTab
                        key={option.value}
                        label={option.label}
                        active={isActive}
                        onPress={() => setTab(option.value)}
                    />
                );
            })}
        </ButtonTabs>
    );
}

/**
 * Header with controls for diff mode.
 */
function HeaderDiff(props: { analyticsContext: analytics.AnalyticsContextSpaceContent }) {
    const { analyticsContext } = props;
    const [diffOptions, setDiffOptions] = useRecoilState(diffOptionsState);

    return (
        <FieldGroup orientation="horizontal" className="min-h-6">
            <Label headless className="text-secondary text-ui-base">
                Show only changed pages
            </Label>
            <Switch
                value={diffOptions.onlyShowChangedPages}
                onValueChange={(value) => {
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'space_diff_edit_mode',
                        diffEnabled: true,
                        diffMode: value ? 'only-changes' : 'all-pages',
                    });

                    setDiffOptions({
                        onlyShowChangedPages: value,
                    });
                }}
            />
        </FieldGroup>
    );
}
